import { Fragment, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Bell, Plus } from 'lucide-react';
import {
  orderService,
  screenService,
  billService,
  apiMessage,
  settingsService,
  type ApiOrderRow,
  type ApiCustomerBill,
  type MenuItem
} from '@kss/api';
import { useAuth } from '@kss/auth';
import { useCurrency } from '@kss/currency';
import {
  Card,
  Button,
  Badge,
  cn,
  useCallWaiter,
  useOrderSession,
  useToast,
  useTable,
  ItemDetailModal,
  type ItemDetailItem,
  type OrderBatch,
  type OrderStatus,
  computeCharges,
  gstBuckets,
  type TaxConfig,
  type OrderType,
  type OrderLine
} from '@kss/ui';
import { useT } from '../i18n/i18n';
import { formatTable } from '../lib/format';
import BillModal from '../components/BillModal';

// The customer lifecycle: Accepted → Preparing → Ready → Served.
const STATUS_ORDER: OrderStatus[] = ['placed', 'preparing', 'ready', 'served'];
const STATUS_LABEL: Record<OrderStatus, string> = {
  placed: 'Accepted', // order received; kitchen hasn't started yet
  preparing: 'Preparing',
  ready: 'Ready',
  served: 'Served'
};
const STATUS_TONE: Record<OrderStatus, 'neutral' | 'warning' | 'info' | 'success'> = {
  placed: 'neutral',
  preparing: 'warning',
  ready: 'info',
  served: 'success'
};
const TYPE_LABEL: Record<OrderType, string> = {
  'dine-in': 'Dine-in',
  takeaway: 'Takeaway',
  packed: 'Packed',
  delivery: 'Delivery'
};

// `done` = the raw API status was "completed" (served & finished). The 4-step tracker
// uses the mapped `served` status, but the badge shows "Completed".
// `cancel` carries the cancellation lifecycle so a cancelled/awaiting order is still SHOWN
// (marked), never dropped from the list.
type TaggedBatch = OrderBatch & { table: string; done: boolean; cancel?: 'requested' | 'cancelled' };

/** A GST rate for a label, e.g. 5 → "5", 2.5 → "2.5". */
const fmtGstRate = (r: number) => (Number.isInteger(r) ? String(r) : r.toFixed(1));

/** Footnote listing the GST rates actually applied, e.g. "Incl. 5%, 18% GST". Says nothing about
 *  GST when none applies (every line inclusive, or no item taxed), so it never quotes a rate that
 *  isn't charged. Always ends with the payment note. */
function gstFootnote(buckets: { rate: number }[]): string {
  const pay = 'pay by UPI, card or at the counter';
  if (buckets.length === 0) return pay.charAt(0).toUpperCase() + pay.slice(1);
  return `Incl. ${buckets.map((b) => `${fmtGstRate(b.rate)}%`).join(', ')} GST · ${pay}`;
}

/** Map the backend's status vocabulary onto the 4-step tracker. */
const API_STATUS: Record<string, OrderStatus> = {
  placed: 'placed',
  preparing: 'preparing',
  ready: 'ready',
  out: 'ready',
  served: 'served',
  completed: 'served',
  paid: 'served' // bill settled — all four steps done, badge shows "Completed"
};

/** Convert a live API order row into the batch shape the tracker renders. */
function mapApiOrder(o: ApiOrderRow, fallbackTable: string): TaggedBatch {
  const cancel =
    o.status === 'cancelled' ? 'cancelled' : o.status === 'cancel-requested' ? 'requested' : undefined;
  return {
    id: o.id.startsWith('#') ? o.id : `#${o.id}`,
    at: o.time,
    type: o.kind === 'dine-in' ? 'dine-in' : o.kind === 'delivery' ? 'packed' : 'takeaway',
    status: API_STATUS[o.status] ?? 'preparing',
    done: o.status === 'completed' || o.status === 'paid',
    cancel,
    items: o.items.map((it, i) => ({
      id: i + 1,
      name: it.name,
      qty: it.qty,
      price: it.qty > 0 ? Math.round(it.amount / it.qty) : it.amount,
      // Carried through so the bill taxes each line at its own rate rather than at one outlet rate.
      gst: it.gst,
      taxInclusive: it.taxInclusive
    })),
    table: o.tableCode || fallbackTable
  };
}

export default function OrderTrack() {
  const tableCode = useTable(); // current dine-in table from the session
  const { format } = useCurrency();
  const { user } = useAuth();
  const orderSession = useOrderSession();
  const navigate = useNavigate();
  const t = useT();
  const waiter = useCallWaiter(tableCode, () => orderService.callWaiter(tableCode));
  const toast = useToast();
  const { pathname } = useLocation();
  const isBillView = pathname.endsWith('/bill'); // Pay-bill tab vs Orders tab
  const [confirming, setConfirming] = useState<string | null>(null);
  // The outlet's GST rate + whether prices already include it. Starts at 0% (no invented rate): a
  // line carries its own rate from the server, and where neither the item nor the outlet has GST
  // configured, none is shown — not a hardcoded 5% on items nobody set any GST for.
  const [tax, setTax] = useState<TaxConfig>({ rate: 0, inclusive: false });
  useEffect(() => {
    settingsService
      .getOutlet()
      .then((o) => {
        const pct = (raw?: string) => Number(String(raw ?? '').trim().replace('%', '')) || 0;
        const split = pct(o.cgst) + pct(o.sgst);
        setTax({ rate: split, inclusive: !!o.taxInclusive });
      })
      .catch(() => {});
  }, []);
  const [billOpen, setBillOpen] = useState(isBillView);
  // The "View Bill" popup — itemised bill + coupon + tax breakdown + request/approve/pay.
  const [showBill, setShowBill] = useState(false);
  // The customer's live bill from the server (request → approve → pay lifecycle).
  const [bill, setBill] = useState<ApiCustomerBill | null>(null);
  const [paying, setPaying] = useState(false);
  // Menu (name → dish) so tapping an ordered line can show its photo/description/discount.
  const [menuByName, setMenuByName] = useState<Map<string, MenuItem>>(new Map());
  const [detailItem, setDetailItem] = useState<ItemDetailItem | null>(null);

  const billRequested = bill != null && bill.status !== 'paid';
  const billApproved = bill?.status === 'approved';

  const tableLabel = formatTable;
  // (Removed: a customer-facing "Merge with T8" button that placed a fabricated dine-in
  // order — 2 Masala Dosa, 2 Gulab Jamun — on table sg-08 and merged it into this bill.
  // Merging tables is a counter operation; it must never invent an order.)

  // Live order status straight from the API — the signed-in customer's own orders
  // (GET /api/order?phone=), refreshed on a short interval so the status advances
  // (Placed → Preparing → Ready → Served) as the kitchen updates it in the panel.
  const [apiBatches, setApiBatches] = useState<TaggedBatch[] | null>(null);
  // Cancelled / awaiting-approval orders are KEPT (mapped with a `cancel` tag) so they stay
  // visible marked-cancelled — the customer and staff can still see what was cancelled.
  async function reloadApiOrders() {
    const phone = user?.phone;
    if (!phone) return;
    try {
      // Scoped server-side by the token, not by a phone we send.
      const rows = await orderService.mine();
      setApiBatches(rows.map((r) => mapApiOrder(r, tableCode)));
    } catch {
      /* offline / API down → keep whatever we had */
    }
  }
  useEffect(() => {
    const phone = user?.phone;
    if (!phone) return;
    let alive = true;
    const load = () =>
      orderService
        .mine()
        .then((rows) => {
          if (!alive) return;
          setApiBatches(rows.map((r) => mapApiOrder(r, tableCode)));
        })
        .catch(() => {
          /* offline / API down → keep the local fallback below */
        });
    load();
    const id = window.setInterval(load, 10000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [user?.phone, tableCode]);

  // Order-level cancel for live API orders. Before the kitchen accepts (status "placed") the
  // order is cancelled outright; once accepted (preparing/ready) we only REQUEST a cancellation
  // and a waiter/admin approves it in the panel. The button drives an inline confirm first.
  const [cancelConfirm, setCancelConfirm] = useState<string | null>(null);
  const [cancelBusy, setCancelBusy] = useState<string | null>(null);
  async function cancelApiOrder(b: TaggedBatch) {
    const accepted = b.status !== 'placed'; // preparing/ready = KOT accepted
    setCancelBusy(b.id);
    try {
      if (accepted) {
        await orderService.requestCancel(b.id, '');
        toast.show('Cancellation requested — a waiter will confirm', 'success');
      } else {
        const ok = await orderService.cancel(b.id);
        toast.show(ok ? 'Order cancelled' : 'Could not cancel — please ask a waiter', ok ? 'success' : 'error');
      }
      await reloadApiOrders();
    } catch {
      toast.show('Could not cancel — please ask a waiter', 'error');
    } finally {
      setCancelBusy(null);
      setCancelConfirm(null);
    }
  }

  // The customer's live bill — polled so the "Pay" button unlocks the moment the
  // waiter/admin approves the amount in the panel.
  useEffect(() => {
    const phone = user?.phone;
    if (!phone) return;
    let alive = true;
    const load = () =>
      billService.mine(phone).then((b) => {
        if (alive) setBill(b);
      });
    load();
    const id = window.setInterval(load, 8000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [user?.phone]);

  // Load the menu once so a tapped order line can open a detail with its real
  // photo, description and discount (looked up by dish name).
  useEffect(() => {
    let alive = true;
    screenService
      .getQrMenu(tableCode)
      .then((d) => {
        if (!alive) return;
        const map = new Map<string, MenuItem>();
        for (const it of d.items) map.set(it.name.trim().toLowerCase(), it);
        setMenuByName(map);
      })
      .catch(() => {
        /* menu unreachable → detail sheet falls back to the line's own data */
      });
    return () => {
      alive = false;
    };
  }, [tableCode]);

  // Tapping an ordered line opens its detail (photo, description, price, discount).
  // The action stays disabled until the bill is approved (item 4 rule).
  function openDetail(line: OrderLine) {
    const dish = menuByName.get(line.name.trim().toLowerCase());
    setDetailItem({
      id: line.id,
      name: line.name,
      desc: dish?.desc ?? 'Tap-through detail for this item.',
      price: dish?.price ?? line.price,
      mrp: dish?.mrp,
      // Never default to veg: an unmatched dish used to render a green veg mark, which
      // mislabels non-veg food. `undefined` shows no mark at all.
      veg: dish?.veg,
      image: dish?.image ?? ''
    });
  }

  async function payNow() {
    if (!bill || bill.status !== 'approved') return;
    setPaying(true);
    try {
      await billService.pay(bill.id, 'upi');
      toast.show('Payment recorded — thank you!', 'success');
      if (user?.phone) setBill(await billService.mine(user.phone));
      setBillOpen(false);
    } catch {
      toast.show('Could not record the payment, try again', 'error');
    } finally {
      setPaying(false);
    }
  }

  async function requestBill(amount: number) {
    if (!user?.phone) return;
    try {
      const b = await billService.request(user.phone, tableCode, amount);
      setBill(b);
      toast.show('Bill requested — a waiter will confirm the amount', 'success');
    } catch (err) {
      // Say what the API said. "Try again" was the only thing this screen ever reported, so a
      // rejected table code looked identical to a dropped connection and retrying never helped.
      toast.show(apiMessage(err, 'Could not request the bill, try again'), 'error');
    }
  }

  // The signed-in customer's OWN running orders from the local session (scoped per
  // customer). Aggregated across every table merged with this one; used as an instant
  // fallback until the API responds (and if it's unreachable).
  const groupTables = orderSession.getGroupTables(tableCode);
  const localBatches: TaggedBatch[] = groupTables.flatMap((tc) =>
    orderSession.getBatches(tc).map((b) => ({ ...b, table: tc, done: b.status === 'served' }))
  );

  // Prefer the real API orders; fall back to the local session only until they load.
  const usingApi = apiBatches != null && apiBatches.length > 0;
  const batches: TaggedBatch[] = usingApi ? apiBatches! : localBatches;
  const isMerged = !usingApi && groupTables.length > 1;

  // No orders for this customer → empty state (never fall back to a demo order,
  // which is what leaked another customer's bill).
  if (batches.length === 0) {
    return (
      <div className='min-h-screen bg-surface-content pb-14 font-sans text-content-primary'>
        <div className='bg-primary text-white'>
          <div className='mx-auto flex w-full max-w-[1200px] items-center gap-3 px-4 py-3'>
            <Link to='/' className='font-display text-sm font-bold'>
              Thali<span className='opacity-80'>OS</span>
            </Link>
            <span className='font-display text-sm font-bold'>{isBillView ? 'Pay bill' : t('orderStatus')}</span>
          </div>
        </div>
        <div className='mx-auto flex max-w-[520px] flex-col items-center gap-4 px-4 py-24 text-center'>
          <div className='text-5xl'>{isBillView ? '🧾' : '🍽️'}</div>
          <div className='font-display text-lg font-bold'>{isBillView ? 'No bill yet' : 'No orders yet'}</div>
          <p className='text-[13px] text-content-muted'>
            {isBillView
              ? 'You have no open bill. Place an order from the menu and settle it here.'
              : 'You haven’t placed any orders. Browse the menu and add a few dishes — your order status and bill will show up here.'}
          </p>
          <Button size='lg' onClick={() => navigate('/order')}>
            Browse the menu →
          </Button>
        </div>
      </div>
    );
  }

  // The "newest" order drives the header/ETA — pick the newest LIVE (non-cancelled) order so a
  // just-cancelled batch doesn't hijack the "Ready in" banner. Falls back to the last batch.
  const liveBatches = batches.filter((b) => b.cancel !== 'cancelled');
  const newest = (liveBatches.length ? liveBatches : batches)[(liveBatches.length ? liveBatches : batches).length - 1]!;
  // No invented prep time. This used to read "~15 min" — a number nothing produced, shown
  // to the customer at 34px under "READY IN". The kitchen does not report an ETA, so the
  // honest answer is the stage the order is actually at.
  const etaLabel =
    newest.status === 'served'
      ? 'Served'
      : newest.status === 'ready'
        ? 'Ready'
        : newest.status === 'preparing'
          ? 'Cooking'
          : 'Accepted';

  // Merged bill across every batch (cancelled orders + approved-cancelled lines excluded).
  // Merge by dish name (+price) — API lines are numbered per-order (id = position), so the same
  // id repeats across orders; keying the merge on id would collapse different dishes into one.
  const merged: OrderLine[] = [];
  for (const b of batches) {
    if (b.cancel === 'cancelled') continue; // a cancelled order isn't billed
    for (const it of b.items) {
      if (it.cancelStatus === 'cancelled') continue;
      const ex = merged.find((m) => m.name === it.name && m.price === it.price);
      if (ex) ex.qty += it.qty;
      else merged.push({ ...it });
    }
  }
  const orderSubTotal = merged.reduce((s, i) => s + i.qty * i.price, 0);
  // Taxed PER LINE, at each item's own rate and inclusivity, falling back to the outlet's for a
  // line that carries none. One outlet-wide rate applied to the whole subtotal charged an 18% item
  // at 5%, and a tax-inclusive line the same as an exclusive one.
  const taxedLines = merged.map((i) => ({ amount: i.qty * i.price, gst: i.gst, inclusive: i.taxInclusive }));
  const orderCharges = computeCharges(orderSubTotal, 0, tax, taxedLines);
  const orderTotal = orderCharges.total;
  // Rate-wise GST rows: one per rate the items actually carry, and none for an item with no GST —
  // a single "GST {outlet rate}%" line mislabelled a mixed bill. Only exclusive buckets are shown
  // (inclusive tax is already inside the price, so an added row would read as an extra charge).
  const orderGstBuckets = gstBuckets(orderSubTotal, 0, tax, taxedLines).filter((b) => !b.inclusive);
  const cancelledLines = batches
    .flatMap((b) => b.items)
    .filter((i) => i.cancelStatus === 'cancelled');

  return (
    <div className='min-h-screen bg-surface-content pb-14 font-sans text-content-primary'>
      <div className='bg-primary text-white'>
        <div className='mx-auto flex w-full max-w-[1200px] items-center gap-3 px-4 py-3'>
          <Link to='/' className='font-display text-sm font-bold'>
            Thali<span className='opacity-80'>OS</span>
          </Link>
          <span className='font-display text-sm font-bold'>{isBillView ? 'Pay bill' : `Order ${newest.id}`}</span>
        </div>
      </div>

      <div className='mx-auto flex w-full max-w-[1200px] flex-col gap-4 p-4 md:flex-row'>
        {/* Bill / actions column */}
        <div className='md:w-[300px] md:flex-shrink-0'>
          <div className='rounded-xl bg-[image:var(--gradient-primary)] p-4 text-white'>
            <div className='text-[10px] font-semibold opacity-90'>
              ORDER {newest.id} · {newest.status === 'served' ? 'STATUS' : 'READY IN'}
            </div>
            <div className='font-display text-[34px] font-extrabold leading-none'>
              {etaLabel}
            </div>
            <div className='mt-1.5 text-[11px] opacity-90'>Chef started · {newest.at}</div>
          </div>

          <Card className='mt-3 p-3.5'>
            {merged.map((i) => (
              <button
                key={i.name}
                type='button'
                onClick={() => openDetail(i)}
                className='flex w-full items-center justify-between gap-2 rounded-md py-1 text-left text-[11.5px] hover:bg-surface-card-hover'
              >
                <span className='min-w-0 truncate text-content-secondary'>
                  {i.qty}× {i.name}
                </span>
                <b className='flex-shrink-0'>{format(i.qty * i.price)}</b>
              </button>
            ))}
            <div className='mt-2 flex justify-between border-t border-border pt-2 font-display text-[12.5px] font-bold'>
              <span>Order total</span>
              <span>{format(orderTotal)}</span>
            </div>
            {cancelledLines.length > 0 && (
              <div className='mt-1.5 flex items-center justify-between text-[10.5px] font-semibold text-danger'>
                <span>
                  {cancelledLines.length} cancelled · not billed
                </span>
                <span className='line-through'>
                  {format(cancelledLines.reduce((s, i) => s + i.qty * i.price, 0))}
                </span>
              </div>
            )}
          </Card>

          <div className='mt-3 flex flex-col gap-2.5'>
            <Button block size='lg' className='gap-1.5' onClick={() => navigate('/order')}>
              <Plus size={16} /> Add more items
            </Button>
            <div className='flex gap-2.5'>
              <Button
                variant='outline'
                block
                className='gap-1.5'
                onClick={waiter.call}
                disabled={waiter.disabled}
              >
                <Bell size={13} /> {waiter.cooldown > 0 ? `Called · ${waiter.cooldown}s` : t('callWaiter')}
              </Button>
              <Button variant='dark' block onClick={() => setShowBill(true)}>
                {billApproved ? 'Pay now →' : billRequested ? 'Bill requested ✓' : `${t('viewBill')} →`}
              </Button>
            </div>
          </div>
        </div>

        {/* Order status — one card per placed order */}
        <Card className='flex-1 p-5'>
          <div className='mb-4 flex items-center justify-between'>
            <span className='flex items-center gap-2 font-display text-sm font-bold'>
              {t('orderStatus')}
              {usingApi && (
                <span className='inline-flex items-center gap-1 rounded-full bg-veg-light px-2 py-0.5 text-[9.5px] font-extrabold text-veg'>
                  ● Live
                </span>
              )}
            </span>
            <span className='text-[11px] font-semibold text-content-muted'>
              {batches.length} order{batches.length > 1 ? 's' : ''} ·{' '}
              {isMerged ? `${groupTables.length} tables` : tableLabel(tableCode)}
            </span>
          </div>

          {isMerged && (
            <div className='mb-3 flex items-center gap-1.5 rounded-lg bg-primary-light px-3 py-2 text-[11.5px] font-bold text-primary'>
              🔗 Merged bill · {groupTables.map(tableLabel).join(' + ')} · billed as one
            </div>
          )}

          <div className='flex flex-col gap-3'>
            {[...batches].reverse().map((b) => (
              <div key={b.id} className='rounded-xl border border-border p-3.5'>
                <div className='flex items-center justify-between'>
                  <div className='min-w-0'>
                    {isMerged && (
                      <span className='mr-1.5 rounded bg-surface-card-hover px-1.5 py-0.5 text-[9.5px] font-bold text-content-secondary'>
                        {tableLabel(b.table)}
                      </span>
                    )}
                    <span className='font-display text-[13px] font-bold'>{b.id}</span>
                    <span className='text-[11px] text-content-muted'>
                      {' '}
                      · {TYPE_LABEL[b.type]}
                      {b.table ? ` · ${tableLabel(b.table)}` : ''} · {b.at}
                    </span>
                  </div>
                  <Badge
                    tone={b.cancel === 'cancelled' ? 'danger' : b.cancel === 'requested' ? 'warning' : STATUS_TONE[b.status]}
                  >
                    {b.cancel === 'cancelled'
                      ? 'Cancelled'
                      : b.cancel === 'requested'
                        ? 'Cancel requested'
                        : b.done
                          ? 'Completed'
                          : STATUS_LABEL[b.status]}
                  </Badge>
                </div>
                <div className='mt-2 flex flex-col gap-1.5'>
                  {b.items.map((line) => {
                    const key = `${b.id}:${line.id}`;
                    // A whole cancelled order strikes through all its lines; the per-line action
                    // column (badge / cancel button) keys off the LINE-level status only.
                    const lineCancelled = line.cancelStatus === 'cancelled';
                    const cancelled = lineCancelled || b.cancel === 'cancelled';
                    const requested = line.cancelStatus === 'requested';
                    return (
                      <div key={line.id} className='flex items-center gap-2 text-[11.5px]'>
                        <span
                          className={cn(
                            'min-w-0 flex-1 truncate',
                            cancelled ? 'text-content-muted line-through' : 'text-content-secondary'
                          )}
                        >
                          {line.qty}× {line.name}
                        </span>
                        {lineCancelled ? (
                          <Badge tone='danger'>Cancelled</Badge>
                        ) : requested ? (
                          // The customer waits for staff to approve. A button labelled
                          // "Approve (waiter/admin)" used to sit here — pressable by the
                          // customer, approving their own cancellation via local session
                          // state. Approval belongs to the panel.
                          <Badge tone='warning'>Awaiting approval</Badge>
                        ) : confirming === key ? (
                          <span className='flex flex-shrink-0 items-center gap-2'>
                            <span className='text-[10px] text-content-secondary'>Cancel item?</span>
                            <button
                              onClick={() => {
                                orderSession.requestCancel(tableCode, b.id, line.id);
                                setConfirming(null);
                              }}
                              className='rounded-md bg-danger px-2 py-0.5 text-[10px] font-bold text-white'
                            >
                              Confirm
                            </button>
                            <button
                              onClick={() => setConfirming(null)}
                              className='text-[10px] font-bold text-content-muted'
                            >
                              Keep
                            </button>
                          </span>
                        ) : !usingApi ? (
                          <button
                            onClick={() => setConfirming(key)}
                            className='flex-shrink-0 text-[10px] font-bold text-danger'
                          >
                            Cancel
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
                <div className='mt-3'>
                  {b.cancel === 'cancelled' ? (
                    <div className='flex items-center gap-1.5 rounded-lg bg-danger/5 px-3 py-2 text-[11.5px] font-semibold text-danger'>
                      ✕ Cancelled — not included in the bill
                    </div>
                  ) : b.cancel === 'requested' ? (
                    <div className='flex items-center gap-1.5 rounded-lg bg-warning-light px-3 py-2 text-[11.5px] font-semibold text-warning'>
                      ⏳ Cancellation requested — a waiter/manager will confirm
                    </div>
                  ) : (
                    <>
                      <StatusSteps status={b.status} />
                      {/* Order-level cancel for live API orders — outright while "placed"
                          (not yet accepted), otherwise a request that staff must approve.
                          Hidden once served/completed. */}
                      {usingApi && !b.done && b.status !== 'served' && (
                        <div className='mt-2.5 flex justify-end'>
                          {cancelConfirm === b.id ? (
                            <span className='flex items-center gap-2'>
                              <span className='text-[10.5px] text-content-secondary'>
                                {b.status === 'placed' ? 'Cancel this order?' : 'Request cancellation?'}
                              </span>
                              <button
                                disabled={cancelBusy === b.id}
                                onClick={() => cancelApiOrder(b)}
                                className='rounded-md bg-danger px-2.5 py-1 text-[10px] font-bold text-white disabled:opacity-60'
                              >
                                {cancelBusy === b.id ? '…' : 'Confirm'}
                              </button>
                              <button
                                onClick={() => setCancelConfirm(null)}
                                className='text-[10px] font-bold text-content-muted'
                              >
                                Keep
                              </button>
                            </span>
                          ) : (
                            <button
                              onClick={() => setCancelConfirm(b.id)}
                              className='text-[10.5px] font-bold text-danger'
                            >
                              {b.status === 'placed' ? 'Cancel order' : 'Request cancellation'}
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className='mt-4 rounded-xl border border-border bg-surface-card-hover px-3.5 py-3 text-[11.5px] leading-relaxed text-content-secondary'>
            Add more items anytime — each new order joins the same table bill and shows its own
            status above. Payment stays open till you settle (UPI · card · counter). Back to{' '}
            <Link to='/order' className='font-semibold text-primary'>
              the menu
            </Link>
            .
          </div>

          {/* Merged-bill notice. Only rendered when tables really are merged — there is no
              customer-side control to merge, so nothing here can create an order. */}
          {isMerged && (
            <div className='mt-3 flex items-center justify-between rounded-xl border border-dashed border-border p-3'>
              <span className='text-[11.5px] text-content-secondary'>
                Merged: {groupTables.map(tableLabel).join(' + ')} — one bill
              </span>
              <button
                onClick={() => orderSession.unmergeGroup(tableCode)}
                className='text-[11px] font-bold text-danger'
              >
                Unmerge
              </button>
            </div>
          )}
        </Card>
      </div>

      {/* Bill sheet — view the running bill, request it at the table, or pay now */}
      {billOpen && (
        <div
          className='fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center'
          onClick={() => setBillOpen(false)}
        >
          <div
            className='w-full max-w-[380px] rounded-t-2xl bg-surface-card p-5 shadow-pop sm:rounded-2xl'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='flex items-center justify-between'>
              <div className='font-display text-base font-bold'>Bill · {tableLabel(tableCode)}</div>
              <button onClick={() => setBillOpen(false)} className='text-lg leading-none text-content-muted'>
                ✕
              </button>
            </div>
            <div className='mt-0.5 text-[11px] text-content-muted'>Order {newest.id}</div>
            <div className='mt-3 flex flex-col gap-1'>
              {merged.map((i) => (
                <button
                  key={i.name}
                  type='button'
                  onClick={() => openDetail(i)}
                  className='flex w-full justify-between rounded-md py-0.5 text-left text-[12.5px] hover:bg-surface-card-hover'
                >
                  <span className='min-w-0 truncate text-content-secondary'>
                    {i.qty}× {i.name}
                  </span>
                  <b className='flex-shrink-0'>{format(i.qty * i.price)}</b>
                </button>
              ))}
            </div>
            <div className='mt-3 space-y-1 border-t border-border pt-2.5 text-[12.5px] text-content-secondary'>
              <div className='flex justify-between'>
                <span>Sub total</span>
                <span>{format(orderSubTotal)}</span>
              </div>
              {/* One row per GST rate the items actually carry (5% food, 18% packaged drink), and
                  none for an item with no GST. Inclusive prices already contain the tax, so those
                  add no row — an amount there would read as an extra charge. */}
              {orderGstBuckets.map((b) => (
                <div key={b.rate} className='flex justify-between'>
                  <span>GST {fmtGstRate(b.rate)}%</span>
                  <span>{format(b.tax)}</span>
                </div>
              ))}
            </div>
            <div className='mt-2 flex justify-between border-t border-border pt-2 font-display text-base font-extrabold text-primary'>
              <span>To pay</span>
              <span>{format(billApproved && bill ? bill.approvedAmount : orderTotal)}</span>
            </div>
            <div className='mt-0.5 text-[10.5px] text-content-muted'>
              {billApproved
                ? 'Amount confirmed by the captain · pay by UPI, card or at the counter'
                : gstFootnote(orderGstBuckets)}
            </div>
            <div className='mt-4 flex flex-col gap-2'>
              {/* request → (captain approves) → pay lifecycle */}
              {bill?.status === 'paid' ? (
                <div className='rounded-xl bg-veg-light py-3 text-center text-[13px] font-bold text-veg'>
                  ✓ Paid — thank you!
                </div>
              ) : billApproved ? (
                <Button block size='lg' onClick={payNow} disabled={paying}>
                  {paying ? 'Processing…' : `Pay ${format(bill?.approvedAmount ?? orderTotal)}`}
                </Button>
              ) : billRequested ? (
                <>
                  <div className='rounded-xl bg-warning-light py-3 text-center text-[12.5px] font-bold text-warning'>
                    ⏳ Bill requested — waiting for the captain to confirm the amount
                  </div>
                  <Button block size='lg' disabled>
                    Pay — available after approval
                  </Button>
                </>
              ) : (
                <Button block size='lg' onClick={() => requestBill(orderTotal)}>
                  Request bill
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Item 4 — tap an ordered line to see its photo / description / price / discount.
          The action stays disabled until the bill is approved; once approved it routes to Pay. */}
      <ItemDetailModal
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onAdd={() => {}}
        readOnly
        actionDisabled={!billApproved}
        actionLabel={billApproved ? 'Pay bill →' : 'Place order'}
        disabledHint={
          billRequested
            ? 'Bill requested — Pay unlocks once the captain approves the amount'
            : 'Request your bill to pay — this unlocks after the captain approves it'
        }
        onAction={() => {
          setDetailItem(null);
          setShowBill(true);
        }}
      />

      {/* View Bill popup — itemised bill + coupon + tax breakdown → Request Bill → (approved) → Pay Now (Cashfree) → print */}
      <BillModal
        open={showBill}
        onClose={() => setShowBill(false)}
        items={merged.map((m) => ({ name: m.name, qty: m.qty, price: m.price }))}
        phone={user?.phone}
        tableCode={tableCode}
        bill={bill}
        onChanged={() => {
          if (user?.phone) billService.mine(user.phone).then(setBill).catch(() => {});
        }}
      />
    </div>
  );
}

/** Compact 4-step progress for one order: Placed → Preparing → Ready → Served. */
function StatusSteps({ status }: { status: OrderStatus }) {
  const idx = STATUS_ORDER.indexOf(status);
  const complete = status === 'served'; // terminal: the order has been served / completed
  const lastIdx = STATUS_ORDER.length - 1;
  return (
    <div className='flex items-start'>
      {STATUS_ORDER.map((s, i) => {
        // When complete, every step is green and the final "Served" pip blinks green.
        const state = complete
          ? i === lastIdx
            ? 'served'
            : 'done'
          : i < idx
            ? 'done'
            : i === idx
              ? 'active'
              : 'todo';
        const circle =
          state === 'served'
            ? 'bg-veg text-white ring-4 ring-veg-light motion-safe:animate-pulse'
            : state === 'done'
              ? 'bg-veg text-white'
              : state === 'active'
                ? 'bg-primary text-white ring-4 ring-primary-light'
                : 'border-2 border-border-strong text-content-muted';
        return (
          <Fragment key={s}>
            <div className='flex-1 text-center'>
              <div
                className={
                  'mx-auto flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-extrabold ' +
                  circle
                }
              >
                {state === 'done' || state === 'served' ? '✓' : state === 'active' ? '●' : i + 1}
              </div>
              <div
                className={
                  'mt-1 text-[10px] font-semibold ' +
                  (state === 'served'
                    ? 'text-veg'
                    : state === 'todo'
                      ? 'text-content-muted'
                      : state === 'active'
                        ? 'text-primary'
                        : '')
                }
              >
                {STATUS_LABEL[s]}
              </div>
            </div>
            {i < STATUS_ORDER.length - 1 && (
              <div className={'mt-3 h-[3px] flex-1 ' + (complete || i < idx ? 'bg-veg' : 'bg-border')} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
